import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import useAutoRunOnFilterChange from '@hooks/useAutoRunOnFilterChange'
import useLatestRun from '@hooks/useLatestRun'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateField, choiceField, boolField } from '@components/reports/specializedFilterFields'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { ReportRunStateContext, useReportHasRun } from '@components/reports/reportRunStateContext'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import CategorySelect from '@components/reports/CategorySelect'
import ControlCheckbox from '@components/common/ControlCheckbox'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { describeFilters, dateRangePart, basisPart } from '@components/reports/reportFilterSummary'
import { balanceSheetSavedCriteria } from '@components/reports/savedRunCriteria'
import { useAccountsIndex } from './accountLinks'
import mergeExportViews from './exportViewMerge'
import {
  buildBalanceSheetModel, toBalanceSheetGridReport, rollupNoteReason, isValidAsOfDate, ROLLUP_UNAVAILABLE_TEXT,
} from './balanceSheetModel'
import useGlCategories, { toFilteredCategories, categoryNames } from '@hooks/useGlCategories'
import usePreferencesStore from '@store/preferencesStore'

/**
 * Balance Sheet (ids 34, 372) — CB renders this report as server-side HTML; cb-api
 * fetches the CSV export and parses it into { headers, rows, meta }, plus (spec 127
 * S1) `hierarchy`: CB's account ids joined onto the CSV row by row.
 *
 * Spec 127 §4.5 (S5): the rows are read by `balanceSheetModel.js` as the layout CB
 * writes them — side headings Assets / Liabilities / Equities as level-0 groups, CB's
 * sections as level-1 groups, `Grand Total: Assets` after the assets, Equity printed
 * flat as V1 prints it, `Grand Total: Liabilities & Equity` as the footer. The old
 * row heuristics that lived here produced three shipped mis-groupings and are gone.
 * Section and Parent Account are never shown; Account Number is text; every total
 * label sits in the Account column, verbatim. A parent account rolls up to the
 * amount on CB's own `Total: <parent>` row, only when CB's ids were joined and the
 * page's own check agrees. Anything the model cannot read renders CB's rows flat,
 * with every column, and says so.
 *
 * GUARDRAIL: balance-sheet ordering is semantic (Assets → Liabilities → Equity).
 * Groups follow CSV order — no cross-section sorting (the grid's statement mode
 * never sorts), and every total passes through from the server CSV (never
 * recomputed). vladdemo's statement is out of balance in CB itself; so is this page.
 */

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF (must render inside the shell's context provider).
// `viewRef` lets a caller capture this grid's getter; `mergeWithRef` names a
// SECOND grid whose view is appended before publishing; `publish={false}` keeps
// a grid from claiming the shell's single ref at all. With none of the three
// passed, this is byte-identical to the single-grid behavior it shipped with.
//
// Spec 127 S5: every Balance Sheet grid renders in the grid's `statementMode` —
// CB's sections never collapse, headers never sort, and a parent account rolls up
// (per parent, or "Roll up all") to CB's own closer amount.
const PublishedGroupedGrid = ({
  report, storageKey, pageStateRef, initialPageState,
  viewRef, mergeWithRef, publish = true, suppressStripActions = false,
}) => {
  const exportCtx = useReportExportView()
  const hasRun = useReportHasRun()
  const ownRef = useRef(null)
  const exportViewRef = viewRef || ownRef
  useEffect(() => {
    if (!exportCtx || !publish) return
    exportCtx.ref.current = () => mergeExportViews(exportViewRef.current?.(), mergeWithRef?.current?.())
    exportCtx.onPublish?.()
  }, [exportCtx, publish, exportViewRef, mergeWithRef])
  // CC-49732: forward the saved-view page-state plumbing to the grid. Without
  // these two props the grid neither publishes its quick-filter state nor
  // restores one, which is why a saved view came back with the wrong per-column
  // filter value on this page while the generic engine restored correctly.
  const grid = (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
      statementMode
    />
  )
  /*
   * Side by side, TWO of these are live at once, and the host publishes its
   * "Rename columns" button through context — so both halves would render one.
   * Two buttons editing the same per-report alias map is worse than ambiguous.
   * The assets half keeps it (it is the half that owns the export ref); the
   * other shadows the value away. hasRun is preserved: suppressing the button
   * must not also change the run control.
   */
  return suppressStripActions
    ? <ReportRunStateContext.Provider value={{ hasRun, stripActions: null }}>{grid}</ReportRunStateContext.Provider>
    : grid
}

// CC-49974: the side-by-side halves are CB's own groups — [Assets] and
// [Liabilities, Equities] + the L&E grand total (balanceSheetModel.js), so the two
// joined in CB's order ARE the single-column statement.
const SIDE_BY_SIDE_FLAG = 'sideBySide'
const STORAGE_KEY = 'report:balance-sheet'
// The two side-by-side halves are LIVE AT THE SAME TIME, so they cannot share a
// preferences key with each other or with the single-column grid: `DataGrid`
// persists column visibility / order / widths / sort / pageSize per key, and three
// grids writing one entry is last-interaction-wins. Sorting the Assets half would
// quietly become the page's persisted sort for the other two.
//
// Tradeoff, deliberate: layout preferences are therefore per-layout — hiding a
// column in the single-column view does not hide it in the halves. That is the
// lesser evil against two mounted grids fighting over one entry, and it keeps each
// half independently sortable (which is the point of the layout). Named views and
// the side-by-side flag itself stay on the BASE key, so a saved view still applies
// to the page as a whole.
const ASSETS_STORAGE_KEY = `${STORAGE_KEY}:assets`
const LIABILITIES_STORAGE_KEY = `${STORAGE_KEY}:liabilities`

const makeDefaults = () => ({
  endDate: dayjs().format('YYYY-MM-DD'),
  isCashBasis: true,
  isShowZeroRows: true,
  showCurrSymbol: true,
  // CC-49973: GL category ids. Empty = ordinary (non-category) balance sheet,
  // which is this page's default — unlike P&L, an empty selection here is a
  // valid, meaningful report, so there is nothing to guard against.
  categoryIds: [],
})

// The wire boundary. `showCurrSymbol` has no control; `filteredCategories` is
// derived from the CategorySelect's ids and must keep going through
// toFilteredCategories (pipe-delimited ints — CB reads an empty value as "no
// category filter").
const toWire = (f) => ({
  endDate: f.endDate,
  isCashBasis: f.isCashBasis,
  isShowZeroRows: f.isShowZeroRows,
  showCurrSymbol: f.showCurrSymbol,
  filteredCategories: toFilteredCategories(f.categoryIds),
})

// A balance sheet is a point-in-time statement → As Of is a single date. Basis is
// mutually exclusive → RADIO; Cash-vs-Accrual must never be multi-selectable.
const FIELDS = [
  dateField({ key: 'asOf', label: 'As Of', valueKey: 'endDate' }),
  choiceField({
    key: 'basis', label: 'Basis', valueKey: 'isCashBasis',
    options: [{ value: 'cash', label: 'Cash' }, { value: 'accrual', label: 'Accrual' }],
    encode: (v) => (v ? 'cash' : 'accrual'),
    decode: (s) => s === 'cash',
  }),
  boolField({ key: 'zeroRows', label: 'Show zero rows', valueKey: 'isShowZeroRows' }),
]

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const BalanceSheetReport = ({ reportId, name = 'Balance Sheet', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  const glCategories = useGlCategories()
  // CC-49974: same-date Assets | Liabilities+Equity layout (V1's `sideBySide`
  // view option — BalanceSheet.cshtml:132-138 printbalancesheetSide vs
  // printbalancesheetInline). Presentation only: SAME run, SAME figures.
  //
  // Read via the whole-store hook (not a selector) to match every other consumer
  // in this repo — the shared test mock returns one instance and ignores
  // selectors, so selector-style access silently yields the store object itself.
  const prefsStore = usePreferencesStore()
  // Mirrored into local state so the toggle re-renders even under the test mock,
  // whose getters are static stubs rather than reactive store reads.
  const [sideBySide, setSideBySide] = useState(() => prefsStore.getPageFlag?.(STORAGE_KEY, SIDE_BY_SIDE_FLAG) ?? false)
  // Spec 131: only the NEWEST run may write the statement, the spinner, the error
  // or the documents' criteria. Spinner immediately when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [data, setData] = useState({ headers: [], rows: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 127 §4.4 step 7: a result STORED before roll-up existed has no `hierarchy`
  // key; the model says so ("saved-before-rollup") only for a stored result.
  const [fromSavedResult, setFromSavedResult] = useState(false)
  // Spec 185 (CC-53435): the reopened job's own stored params — read ONLY to state the
  // saved run's criteria on the documents. Never seeded into `filters`: that would fire
  // useAutoRunOnFilterChange and replace the saved statement with a fresh run.
  const [savedParams, setSavedParams] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-49839: bump to remount the grid after a saved view writes layout prefs.
  const [gridKey, setGridKey] = useState(0)
  // CC-49732: the grid publishes its quick-filters + toggles here for the Views
  // menu to capture; appliedGridStateRef carries a restored view's state into the
  // remounted grid (the grid reads initialPageState in its useState initialisers,
  // so it is MOUNT-only — hence the gridKey bump in applyView below).
  const pageStateRef = useRef(null)
  const appliedGridStateRef = useRef(null)
  // CC-49974: the liabilities half's export view, appended to the assets half's
  // before publishing (see mergeExportViews).
  const liabilitiesViewRef = useRef(null)
  // Spec 127 §4.5: roll-up state lives in each half's grid. The liabilities half
  // publishes its page state here so the page can take the UNION of both halves'
  // rolled parents for a saved view and across a layout toggle; `carriedRolledRef`
  // is that set, captured at the toggle, seeded into whichever grid mounts next.
  const liabilitiesPageStateRef = useRef(null)
  const carriedRolledRef = useRef(null)
  const halvesMountedRef = useRef(false)
  const accountsIndex = useAccountsIndex() // CC-49837

  // The parents currently rolled up, across every mounted grid, in CB order
  // (assets half first). Only a MOUNTED liabilities half is read — its ref keeps
  // the closure of the last one that unmounted.
  const currentRolledIds = () => {
    const own = pageStateRef.current?.()?.rolledIds || []
    const other = halvesMountedRef.current ? (liabilitiesPageStateRef.current?.()?.rolledIds || []) : []
    return [...own, ...other.filter((id) => !own.includes(id))]
  }
  // What the Views menu saves: the (single or assets-half) grid's page state, with
  // the rolled set widened to both halves.
  const currentGridState = () => {
    const state = pageStateRef.current?.() || null
    if (!state || !halvesMountedRef.current) return state
    return { ...state, rolledIds: currentRolledIds() }
  }
  // The page state a grid mounts with: a restored view's, with the rolled set a
  // layout toggle carried over winning. The liabilities half gets `{ rolledIds }`
  // only — no other page state, as before (its quick-filters were never saved).
  const seedFor = (half) => {
    const applied = appliedGridStateRef.current
    const carried = carriedRolledRef.current
    const rolledIds = carried ?? applied?.rolledIds ?? null
    if (half === 'liabilities') return rolledIds ? { rolledIds } : null
    if (!applied && !carried) return null
    return { ...(applied || {}), ...(rolledIds ? { rolledIds } : {}) }
  }
  const toggleSideBySide = (on) => {
    carriedRolledRef.current = currentRolledIds()
    setSideBySide(on)
    prefsStore.setPageFlag?.(STORAGE_KEY, SIDE_BY_SIDE_FLAG, on)
  }

  // Built EXPLICITLY, not spread from `filters` — `categoryIds` is an array and
  // would land on the wire as a stringified query param. `filteredCategories` is
  // the parameter CB actually reads (DownloadBalanceSheetCSV), pipe-delimited.
  const params = useMemo(() => toWire(filters), [filters])

  // Spec 127 §4.5 / Q5: an empty or invalid As Of refuses the run with a note — CB
  // answers a bad date with TODAY's statement, a plausible wrong one. cb-api's 400
  // is the backstop; this is the UX. Keyed on the wire value CB would receive.
  const asOfRefused = !isValidAsOfDate(params.endDate)

  // CC-50029: the exported CSV/Excel/PDF states its as-of date and basis. A
  // balance sheet with no as-of date can't be told apart from any other run.
  //
  // Spec 131: split in two, because the halves answer different questions. The
  // RUN part is what the request was issued with, so a run captures it when it
  // STARTS and the documents keep stating it until another run's rows replace
  // these. The LAYOUT part is display state — toggling side-by-side re-renders the
  // same rows without a run — so it is composed live onto whichever run line is
  // current, never frozen with it (freezing it stamped a file exported from the
  // split screen "single column" because the run had started before the toggle).
  const runFiltersLine = useMemo(
    () => describeFilters(
      dateRangePart(null, filters.endDate),
      basisPart(filters.isCashBasis),
      filters.isShowZeroRows ? 'Including zero balances' : null,
      // CC-49973: a category-scoped balance sheet must say so on the export.
      filters.categoryIds.length
        ? `Categories: ${categoryNames(glCategories.categories, filters.categoryIds)}`
        : null,
    ),
    [filters, glCategories.categories]
  )
  // The export deliberately stays the CANONICAL single-column statement even
  // while the screen is side-by-side: two export shapes for one statement is
  // a support burden, and the server export (the authoritative one) has only
  // ever had the one shape. But a user who exports from the side-by-side view
  // gets a file that does not look like their screen, so the file says which
  // layout produced it rather than leaving them to wonder whether the export
  // dropped a half. The rows and figures are identical either way —
  // mergeExportViews rejoins the two halves in CB's own order.
  const layoutPart = sideBySide ? 'Layout: Side-by-side (export is the full statement, single column)' : null
  const filtersSummary = useMemo(() => describeFilters(runFiltersLine, layoutPart), [runFiltersLine, layoutPart])

  const runReport = useCallback(() => {
    // Guard BEFORE the fetch: a reopened saved result keeps its own date, but a
    // fresh run with no valid as-of date must not reach CB. Spec 131: the refusal
    // also retires a run still in flight; no error (its own note says why).
    if (asOfRefused && !jobRef.current) { invalidateRun(); return undefined }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => {
          setSavedAsOf(st?.createdAt || null)
          setFromSavedResult(true)
          setSavedParams(st?.savedParams ?? null)
          setData(r?.data || { headers: [], rows: [] })
        }
      }
      const result = await accountingReportService.balanceSheet.run(params, { reportId })
      return () => {
        // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
        setSavedAsOf(null)
        setFromSavedResult(false)
        setData(result || { headers: [], rows: [] })
      }
    }, { criteria: { filtersSummary: runFiltersLine } })
  }, [params, reportId, asOfRefused, startRun, invalidateRun, runFiltersLine])

  // The committed run's criteria with TODAY's layout on the end (see
  // `runFiltersLine` above) — null until a run has committed, as the shell expects.
  const ranCriteria = useMemo(
    () => (ran ? { ...ran.criteria, filtersSummary: describeFilters(ran.criteria?.filtersSummary, layoutPart) } : null),
    [ran, layoutPart]
  )

  // CC-49835: first load + re-run whenever a filter changes (debounced so the
  // date input doesn't fire a fetch per keystroke). The Run button stays for
  // an explicit re-run of the same filters.
  useAutoRunOnFilterChange(runReport, params)

  // CC-49839: applying a saved view restores the run filters (the auto-run
  // effect re-fetches) and remounts the grid so it re-reads the layout the
  // views menu wrote back into preferencesStore.
  const applyView = useCallback((view) => {
    if (view?.filters && Object.keys(view.filters).length) {
      setFilters((f) => ({ ...f, ...view.filters }))
    }
    // CC-49732: v2 views also carry the grid's page state (quick-filters +
    // toggles); seed it for the remount. v1 views have no pageState and keep
    // their original filters-and-layout-only behavior.
    appliedGridStateRef.current = view?.v >= 2 ? (view.pageState?.grid || null) : null
    // Spec 127 §4.7: the view's rolled set replaces whatever a layout toggle carried.
    carriedRolledRef.current = null
    setGridKey((k) => k + 1)
  }, [])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS). `params`
  // above stays the wire boundary — including `showCurrSymbol`, which has no
  // control, and `filteredCategories`, which CategorySelect feeds.
  //
  // CategorySelect stays a PAGE control in the action row rather than becoming a
  // filter field: CB pivots the CSV into one column per selected category and
  // retitles the report itself, so the picker changes the report's SHAPE, not
  // just its scope. Side-by-side is presentation only (same run, same figures).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      disabled={asOfRefused}
      queue={{
        reportId,
        name,
        // Mirrors accountingReportService.balanceSheet.run: an ACCOUNTING job
        // (GET replay), key 'balance-sheet' — per-user server-side (bank-account
        // security).
        kind: 'accounting',
        accountingKey: 'balance-sheet',
        endpoint: (f) => accountingEndpoint('/reports/accounting/balance-sheet/data', toWire(f)),
        filters: (f) => toWire(f),
        // Spec 194: the emailed copy can carry this page's own export.
        attachFormats: accountingAttachFormats('/reports/accounting/balance-sheet/data'),
      }}
      actions={(
        <>
          {/* CC-49973: Category Balance Sheet. Unlike P&L this needs no mode
              selector — CB pivots the CSV into one column per selected category
              and retitles the report "Category Balance Sheet" itself, so the
              picker alone is the whole control. An empty selection is the
              ordinary statement. */}
          {glCategories.trackCategories && (
            <CategorySelect
              categories={glCategories.categories}
              value={filters.categoryIds}
              onChange={(ids) => setFilters((f) => ({ ...f, categoryIds: ids }))}
            />
          )}
          {/* CC-49974: presentation-only layout toggle — same run, same figures.

              spec 121 gap 2 (2026-09-09), spec 117 §5 G3: this was a bare
              `<input type="checkbox">` with no className at all — the FIFTH native
              checkbox on a report screen and the one nobody had listed. Unlike the
              other four it painted Chrome's own #767676 box, so it never failed
              contrast; it was purely a consistency gap, and a visible one. It sits
              in the same `actions` cluster as CategorySelect and ReportViewsMenu,
              whose carets spec 117 swept to the outline chevron — so this report
              showed two freshly-swept controls beside a browser default. Reports 34
              and 372 both route to cbAccountingBalanceSheet, so it is two reports,
              not one.
              Behaviour unchanged: same wrapping <label> (so `getByLabelText(
              /Side-by-side layout/i)`, which seven tests use, still resolves), same
              real input, same `e.target.checked`, no `hit24` because the label
              supplies the target. */}
          <label className="flex items-center gap-1.5 text-sm cursor-pointer whitespace-nowrap">
            <ControlCheckbox
              checked={sideBySide}
              onChange={(e) => toggleSideBySide(e.target.checked)}
            />
            Side-by-side layout
          </label>
          {/* CC-49732: getPageState is what makes a saved view v2 and captures the
              grid's quick-filters/toggles so they can be restored. */}
          <ReportViewsMenu
            storageKey={STORAGE_KEY}
            currentFilters={filters}
            onApply={applyView}
            getPageState={() => ({ grid: currentGridState() })}
          />
        </>
      )}
    />
  )

  // Spec 127 §4.5: CB's rows read as the layout CB writes them (balanceSheetModel.js).
  // `fromSavedResult` only changes what a MISSING hierarchy means.
  const model = useMemo(
    () => buildBalanceSheetModel(data, { savedResult: fromSavedResult }),
    [data, fromSavedResult]
  )
  // Spec 185 (CC-53435): a reopened job's criteria line — the queue drawer's own builder
  // (savedRunCriteria.js) over the job's params and the stored result's headers, with
  // TODAY's layout on the end exactly as `ranCriteria` adds it to a live run's line.
  // Null (the shell's "not recorded") when the params cannot state the as-of date and basis.
  const savedRunLine = useMemo(
    () => (savedAsOf != null ? balanceSheetSavedCriteria(savedParams, model.headers) : null),
    [savedAsOf, savedParams, model.headers]
  )
  const gridReport = useMemo(
    () => toBalanceSheetGridReport(model, { layout: 'single', accountsIndex }),
    [model, accountsIndex]
  )

  // CC-49974: the ONE parsed run laid out in two columns — CB's Assets group on the
  // left; its Liabilities and Equities groups plus the L&E grand total on the right.
  // Each half keeps CB's own statement total (Grand Total: Assets closes the Assets
  // group); nothing is re-aggregated and no second run is made. A statement the
  // model could not read has no halves: the single-column fallback shows instead,
  // and the caller says why.
  const sideReports = useMemo(() => {
    if (!sideBySide || !model.ok) return null
    return {
      assets: toBalanceSheetGridReport(model, { layout: 'assets', accountsIndex }),
      liabilities: toBalanceSheetGridReport(model, { layout: 'liabilities', accountsIndex }),
    }
  }, [sideBySide, model, accountsIndex])
  // Read only by event handlers (a layout toggle, a view save), so committing it
  // in an effect is soon enough and keeps the render pure.
  useEffect(() => { halvesMountedRef.current = !!sideReports }, [sideReports])
  // The toggle is on but the statement could not be split — say so instead of
  // silently ignoring the checkbox.
  const sideBySideUnavailable = sideBySide && !sideReports && !model.empty
  // Spec 127 §4.5: CB's rows arrived in a layout the model does not recognise.
  const shapeUnrecognised = !model.ok && !model.empty
  // …or roll-up is unavailable on a statement that has a parent to roll up.
  const rollupReason = rollupNoteReason(model)

  const note = (testId, children) => (
    <div
      role="status"
      data-testid={testId}
      className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      <span aria-hidden="true" className="mt-px font-semibold">ⓘ</span>
      <span>{children}</span>
    </div>
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ranCriteria} runKey={runKey}
      filterBar={filterBar} summaryData={data}
      filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? (savedRunLine ? describeFilters(savedRunLine, layoutPart) : null) : undefined}
      download={(format, getToken) => accountingReportService.balanceSheet.download(params, format, getToken)}
    >
      {/* Spec 127 §4.5 / §8: refuse, never substitute, a missing as-of date. */}
      {asOfRefused && note('bs-as-of-refused', 'Choose an as-of date.')}
      {shapeUnrecognised && note(
        'bs-shape-unrecognised',
        'This Balance Sheet arrived in a shape cb2 does not recognise, so CB’s rows are shown exactly as sent, with every column.'
      )}
      {rollupReason && note('bs-rollup-unavailable', `Roll-up is unavailable for this run: ${ROLLUP_UNAVAILABLE_TEXT[rollupReason]}`)}
      {sideBySideUnavailable && note('bs-side-by-side-unavailable', (
        <>
          <strong className="font-semibold">Showing the single-column layout.</strong>{' '}
          CB&apos;s rows are shown flat, as sent, so there are no Assets and Liabilities &amp;
          Equity halves to lay side by side.
        </>
      ))}
      {sideReports ? (
        // CC-49974: V1's `sideBySide` view option — Assets on the left,
        // Liabilities & Equity on the right, for the SAME as-of date. Stacks to
        // one column below `lg` so it stays readable on a narrow viewport.
        // NOTE: the export/PDF stays the canonical single-column statement — one
        // statement should not have two export shapes. The server CSV/Excel is
        // untouched by the toggle, and the client PDF is built from the two halves
        // rejoined (mergeExportViews), NOT from whichever grid mounted last.
        // Spec 127 S5: the halves' own <h3> headings are gone — each grid's groups
        // carry CB's headings (Assets / Liabilities / Equities); the regions stay.
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section aria-label="Assets">
            <PublishedGroupedGrid
              key={`assets-${gridKey}`}
              report={sideReports.assets}
              storageKey={ASSETS_STORAGE_KEY}
              // The assets half owns the shell's export ref and appends the other
              // half; it also carries the Views menu's page-state capture, so
              // `getPageState()` reads a MOUNTED grid rather than the closure the
              // unmounted single grid left behind.
              mergeWithRef={liabilitiesViewRef}
              pageStateRef={pageStateRef}
              // Pre-existing (CC-49732 / spec 127 §4.5): a MOUNT-only seed read
              // behind a gridKey bump. Surfaced, not introduced, by spec 131 —
              // the React Compiler lint skipped this whole component while its
              // runReport held a try/finally, which now lives in useLatestRun.
              // eslint-disable-next-line react-hooks/refs
              initialPageState={seedFor('assets')}
            />
          </section>
          <section aria-label="Liabilities and equity">
            <PublishedGroupedGrid
              key={`liabilities-${gridKey}`}
              report={sideReports.liabilities}
              storageKey={LIABILITIES_STORAGE_KEY}
              viewRef={liabilitiesViewRef}
              pageStateRef={liabilitiesPageStateRef}
              // Pre-existing; see the assets half above.
              // eslint-disable-next-line react-hooks/refs
              initialPageState={seedFor('liabilities')}
              publish={false}
              suppressStripActions
            />
          </section>
        </div>
      ) : (
        <PublishedGroupedGrid
          key={gridKey}
          report={gridReport}
          storageKey={STORAGE_KEY}
          pageStateRef={pageStateRef}
          // Pre-existing; see the assets half above.
          // eslint-disable-next-line react-hooks/refs
          initialPageState={seedFor('single')}
        />
      )}
    </SpecializedReportShell>
  )
}

export default BalanceSheetReport
